import type { ManagementPolicy, ManagementPreset, ManagementTrigger } from "../schemas/policy.js";
import type { Rule, RuleAction, RuleSet, RuleTrigger } from "../schemas/rules.js";
import { POLICY_BOUNDS, POLICY_DEFAULTS } from "./bounds.js";

/**
 * Re-exported so the web imports everything it needs from this one subpath. Type-only, so
 * nothing follows them into `schemas/policy.ts` at runtime and zod stays out of the bundle.
 */
export type {
  FollowChannelManagement,
  ManagementPolicy,
  ManagementPreset,
  ManagementTrigger,
} from "../schemas/policy.js";
export type { Rule, RuleAction, RuleSet, RuleTrigger } from "../schemas/rules.js";
export { POLICY_BOUNDS, POLICY_DEFAULTS } from "./bounds.js";

/**
 * M14a — turning a named policy into the rules the engine actually runs.
 *
 * Deliberately zod-free and type-only in its imports, so the web can import it on the
 * `@app/shared/policy` subpath to render the preview without pulling zod into the bundle.
 * That preview is not a second description of what a preset does: it is `compilePolicy`
 * itself, run in the browser, so the card cannot promise something the server would not
 * store.
 *
 * **Compilation happens at WRITE, never at read.** The route compiles and stores `policy`
 * and `rules` in one statement; `loadEffectiveRuleConfig`, the rule engine and
 * `policy_snapshot` are untouched. Compiling on read would put this code on the path every
 * live signal's management flows through, and — the deciding argument — a later change here
 * would silently rewrite what is running on already-open positions, with no DB write and no
 * audit row. That is the same objection `execute.ts` raises against a mutable
 * `policy_snapshot`: an attribution record that changes after the fact is worse than none.
 *
 * The compiler is **not injective**, and this is the one subtlety worth carrying: `ladder`
 * and `be_buffer` with the TP2 checkbox emit identical rules, as do `be_at_tp1` and
 * `be_buffer` with a zero buffer. So `inferPolicy` cannot be a literal inverse. What holds
 * instead, and is asserted over the whole parameter grid in the tests, is that inference
 * recovers a policy with the SAME compiled behaviour (`canonicalPolicy`), and that reading
 * a rule set into the card and saving it back therefore never changes what runs.
 */

/**
 * The cold-start policy as a plain object.
 *
 * The literal lives here rather than beside the zod schema because the web needs a starting
 * point when someone switches a hand-written rule set to Presets, and it may not import zod.
 * `DEFAULT_MANAGEMENT_POLICY` is `managementPolicySchema.parse` of exactly this, so the
 * schema still validates it against `POLICY_BOUNDS` — one literal, checked, not two that can
 * drift.
 */
export const BASE_POLICY: ManagementPolicy = {
  version: 1,
  preset: POLICY_DEFAULTS.preset,
  beOffsetPips: POLICY_DEFAULTS.beOffsetPips,
  bufferSpreads: POLICY_DEFAULTS.bufferSpreads,
  stopAtR: POLICY_DEFAULTS.stopAtR,
  trailToTp1AfterTp2: POLICY_DEFAULTS.trailToTp1AfterTp2,
  partialClose: {
    enabled: POLICY_DEFAULTS.partialEnabled,
    at: POLICY_DEFAULTS.partialAt,
    pct: POLICY_DEFAULTS.partialPct,
  },
  trailing: {
    enabled: POLICY_DEFAULTS.trailingEnabled,
    at: POLICY_DEFAULTS.trailingAt,
    distancePips: POLICY_DEFAULTS.trailingDistancePips,
  },
  cancelPendingOnSignalClose: POLICY_DEFAULTS.cancelPendingOnSignalClose,
  closeRemainingOnSignalClose: POLICY_DEFAULTS.closeRemainingOnSignalClose,
};

/** Emission order. Matches `ruleTriggerSchema` so a compiled set reads top to bottom. */
const TRIGGER_ORDER: readonly RuleTrigger[] = [
  "ENTRY_FILLED",
  "TP1_HIT",
  "TP2_HIT",
  "TP3_HIT",
  "SL_HIT",
  "SIGNAL_CLOSED",
];

/** The stop-move action types a preset can put at TP1. */
type StopMove = Extract<RuleAction, { type: "MOVE_SL_TO_BE" | "MOVE_SL_TO_R" | "MOVE_SL_TO_TP" }>;

export type PolicyParamKey = "beOffsetPips" | "bufferSpreads" | "stopAtR";

/**
 * Which parameters each preset actually reads. The card renders exactly these inputs, and
 * `inferPolicy` resets everything else to its default — those two facts are the same fact,
 * which is why this table is here and not in the component.
 */
export const PRESET_PARAMS: Record<ManagementPreset, readonly PolicyParamKey[]> = {
  hold: [],
  be_at_tp1: ["beOffsetPips"],
  be_buffer: ["beOffsetPips", "bufferSpreads"],
  half_risk: ["stopAtR"],
  ladder: ["beOffsetPips", "bufferSpreads"],
  custom: [],
};

/**
 * The one compile site, used by the server on write and by the web for the preview.
 *
 * Throws on `custom`: Advanced mode stores the raw rules with `policy: null`, and inventing
 * a rule set for a policy that says "these rules were hand-written" would overwrite them.
 */
export function compilePolicy(policy: ManagementPolicy): RuleSet {
  if (policy.preset === "custom") {
    throw new Error(
      "a custom policy has no compiled form — Advanced mode stores the raw rules with policy: null",
    );
  }

  type Bucket = { partial?: RuleAction; stop?: StopMove; trailing?: RuleAction };
  const buckets = new Map<ManagementTrigger, Bucket>();
  const at = (trigger: ManagementTrigger): Bucket => {
    const existing = buckets.get(trigger);
    if (existing) return existing;
    const fresh: Bucket = {};
    buckets.set(trigger, fresh);
    return fresh;
  };

  const stop = presetStopMove(policy);
  if (stop) at("TP1_HIT").stop = stop;

  // Under `ladder` the second rung IS the preset, so the checkbox is not consulted — it
  // renders checked and disabled, and canonical form leaves the stored flag at its default.
  if (policy.trailToTp1AfterTp2 || policy.preset === "ladder") {
    at("TP2_HIT").stop = { type: "MOVE_SL_TO_TP", tpSlot: 1 };
  }

  if (policy.partialClose.enabled) {
    at(policy.partialClose.at).partial = { type: "PARTIAL_CLOSE", pct: policy.partialClose.pct };
  }

  if (policy.trailing.enabled) {
    // A trail REPLACES the stop move at its own trigger rather than joining it. Each action
    // becomes its own `apply_rule` job and a trailed order stands down from fixed stop
    // moves, so emitting both at one trigger is a race whose loser is silently refused.
    // Replacing removes the race by construction.
    const bucket = at(policy.trailing.at);
    delete bucket.stop;
    bucket.trailing = { type: "SET_TRAILING", distancePips: policy.trailing.distancePips };
  }

  const rules: Rule[] = [];
  for (const trigger of TRIGGER_ORDER) {
    const bucket = buckets.get(trigger as ManagementTrigger);
    if (!bucket) continue;
    const actions = [bucket.partial, bucket.stop, bucket.trailing].filter(
      (action): action is RuleAction => action !== undefined,
    );
    if (actions.length > 0) rules.push({ trigger, actions });
  }

  // SIGNAL_CLOSED is last in TRIGGER_ORDER and nothing else writes into it, so appending
  // here keeps the emission order without threading a second shape through the buckets.
  const onClose: RuleAction[] = [];
  if (policy.closeRemainingOnSignalClose) onClose.push({ type: "CLOSE_REMAINING" });
  if (policy.cancelPendingOnSignalClose) onClose.push({ type: "CANCEL_PENDING" });
  if (onClose.length > 0) rules.push({ trigger: "SIGNAL_CLOSED", actions: onClose });

  return rules;
}

function presetStopMove(policy: ManagementPolicy): StopMove | undefined {
  switch (policy.preset) {
    case "hold":
      return undefined;
    case "be_at_tp1":
      return { type: "MOVE_SL_TO_BE", offsetPips: policy.beOffsetPips, bufferSpreads: 0 };
    case "be_buffer":
    case "ladder":
      return {
        type: "MOVE_SL_TO_BE",
        offsetPips: policy.beOffsetPips,
        bufferSpreads: policy.bufferSpreads,
      };
    case "half_risk":
      return { type: "MOVE_SL_TO_R", rMultiple: policy.stopAtR };
    case "custom":
      // Unreachable: `compilePolicy` rejects custom before this is called.
      throw new Error("a custom policy has no compiled form");
  }
}

/**
 * Read a stored rule set back as a policy, or `null` when no preset can express it.
 *
 * `null` is a perfectly good answer — it means Advanced mode, and nothing is lost. What
 * would NOT be acceptable is returning a policy that compiles to something different from
 * the rules given, because the card would then silently rewrite a channel's management the
 * first time it was opened and saved. Every refusal below exists for that reason, so each
 * one is a shape the compiler could not have produced.
 *
 * This is also what removes the need for a data backfill: the seeded `DEFAULT_RULE_SET`
 * and anything the compiler wrote before the policy column existed open in the preset card.
 */
export function inferPolicy(rules: RuleSet): ManagementPolicy | null {
  const seen = new Set<RuleTrigger>();

  let tp1Stop: StopMove | undefined;
  let hasTp2Step = false;
  let partial: { at: ManagementTrigger; pct: number } | undefined;
  let trailing: { at: ManagementTrigger; distancePips: number } | undefined;
  let closeRemaining = false;
  let cancelPending = false;

  for (const rule of rules) {
    if (seen.has(rule.trigger)) return null;
    seen.add(rule.trigger);

    if (rule.trigger === "SIGNAL_CLOSED") {
      const parsed = parseCloseRule(rule);
      if (!parsed) return null;
      closeRemaining = parsed.closeRemaining;
      cancelPending = parsed.cancelPending;
      continue;
    }

    if (rule.trigger !== "TP1_HIT" && rule.trigger !== "TP2_HIT") return null;

    const parsed = parseManagementRule(rule);
    if (!parsed) return null;

    if (parsed.partial !== undefined) {
      if (partial) return null;
      partial = { at: rule.trigger, pct: parsed.partial };
    }
    if (parsed.trailing !== undefined) {
      if (trailing) return null;
      trailing = { at: rule.trigger, distancePips: parsed.trailing };
    }
    if (parsed.stop) {
      if (rule.trigger === "TP1_HIT") {
        // MOVE_SL_TO_TP at TP1 is an Advanced construction — no preset emits it.
        if (parsed.stop.type === "MOVE_SL_TO_TP") return null;
        tp1Stop = parsed.stop;
      } else {
        if (parsed.stop.type !== "MOVE_SL_TO_TP" || parsed.stop.tpSlot !== 1) return null;
        hasTp2Step = true;
      }
    }
  }

  const preset = presetFor(tp1Stop, hasTp2Step);
  if (preset === null) return null;

  const policy: ManagementPolicy = {
    version: 1,
    preset,
    beOffsetPips:
      tp1Stop?.type === "MOVE_SL_TO_BE" ? tp1Stop.offsetPips : POLICY_DEFAULTS.beOffsetPips,
    bufferSpreads:
      preset === "be_buffer" || preset === "ladder"
        ? (tp1Stop as Extract<RuleAction, { type: "MOVE_SL_TO_BE" }>).bufferSpreads
        : POLICY_DEFAULTS.bufferSpreads,
    stopAtR: tp1Stop?.type === "MOVE_SL_TO_R" ? tp1Stop.rMultiple : POLICY_DEFAULTS.stopAtR,
    // `ladder` forces the step itself, so under it the flag is a parameter the compiler
    // never reads and canonical form leaves it at its default.
    trailToTp1AfterTp2: hasTp2Step && preset !== "ladder",
    partialClose: {
      enabled: partial !== undefined,
      at: partial?.at ?? POLICY_DEFAULTS.partialAt,
      pct: partial?.pct ?? POLICY_DEFAULTS.partialPct,
    },
    trailing: {
      enabled: trailing !== undefined,
      at: trailing?.at ?? POLICY_DEFAULTS.trailingAt,
      distancePips: trailing?.distancePips ?? POLICY_DEFAULTS.trailingDistancePips,
    },
    cancelPendingOnSignalClose: cancelPending,
    closeRemainingOnSignalClose: closeRemaining,
  };

  return withinPolicyBounds(policy) ? policy : null;
}

/**
 * `[PARTIAL_CLOSE?, stop move?, SET_TRAILING?]`, in that order and nothing else — the exact
 * shape `compilePolicy` emits for a managed trigger.
 */
function parseManagementRule(
  rule: Rule,
): { partial?: number; stop?: StopMove; trailing?: number } | null {
  let partial: number | undefined;
  let stop: StopMove | undefined;
  let trailing: number | undefined;
  // 0 = nothing yet, 1 = past the partial, 2 = past the stop move, 3 = past the trail.
  let phase = 0;

  for (const action of rule.actions) {
    // A preset speaks for the whole signal; a slot filter is an Advanced construction.
    if (action.slots !== undefined) return null;

    switch (action.type) {
      case "PARTIAL_CLOSE":
        if (phase > 0) return null;
        partial = action.pct;
        phase = 1;
        break;
      case "MOVE_SL_TO_BE":
      case "MOVE_SL_TO_R":
      case "MOVE_SL_TO_TP":
        if (phase > 1) return null;
        stop = action;
        phase = 2;
        break;
      case "SET_TRAILING":
        if (phase > 2) return null;
        trailing = action.distancePips;
        phase = 3;
        break;
      default:
        return null;
    }
  }

  // The compiler REPLACES a stop move with a trail at the same trigger, so a rule holding
  // both is not something it could have written. Reading it as a preset would drop one of
  // the two on the next save.
  if (stop !== undefined && trailing !== undefined) return null;

  return { partial, stop, trailing };
}

/** `[CLOSE_REMAINING?, CANCEL_PENDING?]`, in that order. */
function parseCloseRule(rule: Rule): { closeRemaining: boolean; cancelPending: boolean } | null {
  let closeRemaining = false;
  let cancelPending = false;
  let phase = 0;

  for (const action of rule.actions) {
    if (action.slots !== undefined) return null;
    if (action.type === "CLOSE_REMAINING") {
      if (phase > 0) return null;
      closeRemaining = true;
      phase = 1;
    } else if (action.type === "CANCEL_PENDING") {
      if (phase > 1) return null;
      cancelPending = true;
      phase = 2;
    } else {
      return null;
    }
  }

  return { closeRemaining, cancelPending };
}

/**
 * Which preset a TP1 stop move names. Where two presets emit the same rule the more
 * specific one wins, so that the name shown always describes the whole rule set: a buffer
 * of zero is `be_at_tp1` rather than `be_buffer` pretending to have one, and a buffered
 * breakeven followed by the TP2 rung is the `ladder` rather than two unrelated choices.
 */
function presetFor(tp1Stop: StopMove | undefined, hasTp2Step: boolean): ManagementPreset | null {
  if (!tp1Stop) return "hold";
  if (tp1Stop.type === "MOVE_SL_TO_R") return "half_risk";
  if (tp1Stop.type !== "MOVE_SL_TO_BE") return null;
  if (tp1Stop.bufferSpreads === 0) return "be_at_tp1";
  return hasTp2Step ? "ladder" : "be_buffer";
}

/**
 * The rule schema is wider than the policy schema in two places on purpose, so an Advanced
 * rule set can hold a value no preset can express. Checked here by hand because this module
 * is zod-free; the bounds themselves are shared with the schema so the two cannot drift.
 */
function withinPolicyBounds(policy: ManagementPolicy): boolean {
  const within = (value: number, bound: { min: number; max: number }) =>
    value >= bound.min && value <= bound.max;

  return (
    within(policy.beOffsetPips, POLICY_BOUNDS.beOffsetPips) &&
    within(policy.bufferSpreads, POLICY_BOUNDS.bufferSpreads) &&
    within(policy.stopAtR, POLICY_BOUNDS.stopAtR) &&
    within(policy.partialClose.pct, POLICY_BOUNDS.partialPct) &&
    policy.trailing.distancePips > POLICY_BOUNDS.trailingDistancePips.min &&
    policy.trailing.distancePips <= POLICY_BOUNDS.trailingDistancePips.max
  );
}

/**
 * The policy in the form its own rules can express: every parameter the compiler does not
 * read reset to its default, and the preset named as specifically as the rules allow.
 *
 * Defined as inference over the compiler's own output rather than as a second copy of the
 * table, so the two cannot drift apart. The throw is an invariant assertion, not a caller
 * error: a compiled rule set that inference cannot read back is a bug in this file.
 */
export function canonicalPolicy(policy: ManagementPolicy): ManagementPolicy {
  const inferred = inferPolicy(compilePolicy(policy));
  if (inferred === null) {
    throw new Error("compilePolicy emitted a rule set inferPolicy cannot read back");
  }
  return inferred;
}

/** One human sentence per compiled rule — what the preview renders. */
export function describeRule(rule: Rule): string {
  const actions = rule.actions.map(describeAction).join(", then ");
  return `${TRIGGER_LABEL[rule.trigger]}: ${actions}.`;
}

const TRIGGER_LABEL: Record<RuleTrigger, string> = {
  ENTRY_FILLED: "When the entry fills",
  TP1_HIT: "When TP1 pays",
  TP2_HIT: "When TP2 pays",
  TP3_HIT: "When TP3 pays",
  SL_HIT: "When the stop is hit",
  SIGNAL_CLOSED: "When the channel closes the signal",
};

function describeAction(action: RuleAction): string {
  const slots = action.slots ? ` (slots ${action.slots.join(", ")} only)` : "";

  switch (action.type) {
    case "MOVE_SL_TO_BE": {
      const offset =
        action.offsetPips === 0
          ? ""
          : action.offsetPips > 0
            ? `, ${action.offsetPips} pips into profit`
            : `, ${Math.abs(action.offsetPips)} pips short of it`;
      const buffer =
        action.bufferSpreads === 0
          ? ""
          : `, held ${action.bufferSpreads}× the current spread on the losing side so a retest cannot tag it`;
      return `move the stop to breakeven${offset}${buffer}${slots}`;
    }
    case "MOVE_SL_TO_TP":
      return `trail the stop up to TP${action.tpSlot}'s level${slots}`;
    case "MOVE_SL_TO_R": {
      if (action.rMultiple === 0) return `move the stop to breakeven (0R)${slots}`;
      if (action.rMultiple < 0) {
        return `move the stop to ${action.rMultiple}R, leaving ${Math.abs(action.rMultiple)}× the original risk still at stake${slots}`;
      }
      return `move the stop to +${action.rMultiple}R, banking ${action.rMultiple}× the original risk${slots}`;
    }
    case "CLOSE_REMAINING":
      return `close whatever is still open${slots}`;
    case "PARTIAL_CLOSE":
      return `close ${action.pct}% of what is still open${slots}`;
    case "SET_TRAILING":
      return `hand the stop to a server-side trailing stop ${action.distancePips} pips behind${slots}`;
    case "CANCEL_PENDING":
      return `cancel any entry that has not filled${slots}`;
  }
}

/**
 * The things that are true of this policy and would otherwise be surprising. Shown under
 * the preview, because a preset card that only lists what it does is how someone ends up
 * believing a checkbox did something it did not.
 */
export function policyCaveats(policy: ManagementPolicy): string[] {
  if (policy.preset === "custom") {
    return ["These rules are edited directly, so no preset describes them."];
  }

  const lines: string[] = [
    "Presets only manage TP1, TP2 and the channel closing the signal. Rules on entry fill, TP3 or the stop being hit are Advanced mode only.",
  ];

  if (
    policy.preset === "hold" &&
    !policy.partialClose.enabled &&
    !policy.trailing.enabled &&
    !policy.trailToTp1AfterTp2
  ) {
    lines.push("Nothing is managed after entry — the stop stays where the signal put it.");
  }

  if (policy.preset === "half_risk") {
    lines.push(
      "R is the distance from entry to the signal's own stated stop, not the padded stop resting at the broker.",
    );
  }

  if (policy.partialClose.enabled) {
    lines.push(
      `Your signals open one order per take-profit, so closing ${policy.partialClose.pct}% at ` +
        `${policy.partialClose.at === "TP1_HIT" ? "TP1" : "TP2"} takes that much off the REMAINING slots — ` +
        "the slot with that target has already closed itself.",
    );
    lines.push(
      "Partial close is unverified on this broker: MT4 may reissue the ticket. It stays off until docs/broker-verification.md says otherwise.",
    );
  }

  if (policy.trailing.enabled) {
    const where = policy.trailing.at === "TP1_HIT" ? "TP1" : "TP2";
    lines.push(
      `The trailing stop replaces the stop move at ${where}, and once it is set that order's stop belongs to MetaApi — later stop rules and app SL edits stand down.`,
    );
    lines.push("While dry run is on, a trail is simulated here rather than set at the broker.");
  }

  return lines;
}
